import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import html2canvas from 'html2canvas';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { first } from 'rxjs/operators';
import { FileUploadService } from './file-upload.service';
import { FileUpload } from '../models/file-upload.model';
import { environment } from 'src/environments/environment';
import { TenantService } from './tenant.service';

export interface NetworkLog {
  timestamp: Date;
  type: 'request' | 'response' | 'error';
  url: string;
  method?: string;
  status?: number;
  body?: any;
}

export interface BugReport {
  id: string;
  timestamp: Date;
  description: string;
  consoleLog: string[];
  networkLogs: NetworkLog[];
  screenshotUrl?: string;
  userAgent: string;
  url: string;
  userEmail?: string;
  appVersion: string;
}

export interface CreateBugReportInput {
  description: string;
  screenshot?: string | null;
  url: string;
  userAgent: string;
  timestamp: string;
  consoleLog: string[];
  networkLogs: NetworkLog[];
}

@Injectable({
  providedIn: 'root'
})
export class BugReportService {
  private consoleLog: string[] = [];
  private networkLogs: NetworkLog[] = [];
  private originalConsole: any = {};
  private tenantId: string = '';
  private tenantService!: TenantService;

  constructor(
    private fns: AngularFireFunctions,
    private auth: AngularFireAuth,
    private fileUploadService: FileUploadService,
    private injector: Injector
  ) {
    this.initializeConsoleTracking();
    // Log the app version on initialization
    console.info(`App Version: ${environment.version}`);
    
    // Defer getting the TenantService to avoid circular dependency
    setTimeout(() => {
      this.tenantService = this.injector.get(TenantService);
      this.tenantService.tenantId$.subscribe(tenantId => {
        if (tenantId) {
          this.tenantId = tenantId;
        }
      });
    });
  }

  private initializeConsoleTracking(): void {
    // Store original console methods
    this.originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info
    };

    // Override console methods
    console.log = (...args) => {
      this.consoleLog.push(`[LOG] ${args.map(arg => this.safeStringify(arg)).join(' ')}`);
      this.originalConsole.log.apply(console, args);
    };

    console.error = (...args) => {
      const errorMessage = args.map(arg => this.safeStringify(arg)).join(' ');
      // Filter out common Angular form validation errors that aren't actionable
      if (!this.shouldIgnoreError(errorMessage)) {
        this.consoleLog.push(`[ERROR] ${errorMessage}`);
      }
      this.originalConsole.error.apply(console, args);
    };

    console.warn = (...args) => {
      this.consoleLog.push(`[WARN] ${args.map(arg => this.safeStringify(arg)).join(' ')}`);
      this.originalConsole.warn.apply(console, args);
    };

    console.info = (...args) => {
      this.consoleLog.push(`[INFO] ${args.map(arg => this.safeStringify(arg)).join(' ')}`);
      this.originalConsole.info.apply(console, args);
    };

    // Track unhandled errors
    window.onerror = (msg, url, line, col, error) => {
      // Filter out common Angular form validation errors that aren't actionable
      if (this.shouldIgnoreError(msg?.toString() || '')) {
        return false;
      }
      this.consoleLog.push(`[UNCAUGHT ERROR] ${msg} at ${url}:${line}:${col}`);
      return false;
    };
  }

  private shouldIgnoreError(errorMessage: string): boolean {
    const ignoredErrorPatterns = [
      // Angular form validation errors that aren't actionable
      "Cannot read properties of null (reading '_rawValidators')",
      "Cannot read properties of undefined (reading '_rawValidators')",
      "Cannot read property '_rawValidators' of null",
      "Cannot read property '_rawValidators' of undefined",
      
      // Other common Angular form errors
      "Cannot read properties of null (reading 'validators')",
      "Cannot read properties of undefined (reading 'validators')",
      
      // Empty or very generic errors
      "\"\"",
      "{}",
      "Unhandled error: {}",
      
      // Calendar/timesheet related errors that might not be critical
      "getCalendarEventsv2",
      
      // Network errors that are handled elsewhere
      "Failed to fetch",
      "NetworkError",
      
      // Very short error messages that provide no context
    ];
    
    return ignoredErrorPatterns.some(pattern => 
      errorMessage.includes(pattern) || 
      errorMessage.trim().length < 5 // Ignore very short error messages
    );
  }

  private safeStringify(obj: any): string {
    const cache = new Set();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (cache.has(value)) {
          // Remove circular reference
          return '[Circular Reference]';
        }
        cache.add(value);
      }
      return value;
    });
  }

  private async uploadScreenshot(screenshotDataUrl: string, reportId: string): Promise<string | undefined> {
    try {
      if (!screenshotDataUrl) {
        return undefined;
      }
      
      // Process the image in chunks to avoid blocking the main thread for too long
      // Convert base64 to blob using async processing
      const response = await fetch(screenshotDataUrl);
      const blob = await response.blob();

      // Create a File object from the blob
      const file = new File([blob], `screenshot-${reportId}.png`, { type: 'image/png' });
      
      // Create FileUpload object
      const fileUpload = new FileUpload(file);
      
      // Upload to Firebase Storage using tenant path
      const storagePath = this.tenantId ? `${this.tenantId}/bug-reports` : 'bug-reports';
      
      // Use a background task for the upload to avoid blocking the UI
      
      const uploadTask = this.fileUploadService.pushFileToStorage(storagePath,  fileUpload);
      
      // Wait for upload to complete and get download URL
      const snapshot = await uploadTask;
      const downloadUrl = await snapshot.ref.getDownloadURL();
      
      return downloadUrl;
    } catch (error) {
      console.error('Failed to upload screenshot:', error);
      return undefined;
    }
  }

  addNetworkLog(log: NetworkLog): void {
    this.networkLogs.push(log);
    // Keep only the last 100 network logs to prevent memory issues
    if (this.networkLogs.length > 100) {
      this.networkLogs = this.networkLogs.slice(-100);
    }
  }

  async createBugReport(input: CreateBugReportInput): Promise<BugReport> {
    // Skip bug report submission in development mode
    if (!environment.production) {
      
      const reportId = Date.now().toString();
      return {
        id: reportId,
        timestamp: new Date(input.timestamp),
        description: input.description,
        consoleLog: [...this.consoleLog, ...input.consoleLog],
        networkLogs: [...this.networkLogs, ...input.networkLogs],
        userAgent: input.userAgent,
        url: input.url,
        userEmail: undefined,
        appVersion: environment.version
      };
    }

    // Ensure user is authenticated
    const user = await this.auth.user.pipe(first()).toPromise();
    if (!user) {
      throw new Error('User must be authenticated to submit a bug report');
    }

    const reportId = Date.now().toString();
    let screenshotUrl: string | undefined;

    if (input.screenshot) {
      screenshotUrl = await this.uploadScreenshot(input.screenshot, reportId);
    }
    
    const report: BugReport = {
      id: reportId,
      timestamp: new Date(input.timestamp),
      description: input.description,
      consoleLog: [...this.consoleLog, ...input.consoleLog],
      networkLogs: [...this.networkLogs, ...input.networkLogs],
      screenshotUrl,
      userAgent: input.userAgent,
      url: input.url,
      userEmail: user.email || undefined,
      appVersion: environment.version
    };

    // Send the bug report using Firebase Function
    try {
      const sendBugReport = this.fns.httpsCallable('sendBugReport');
      const result = await sendBugReport(report).toPromise();
      
      this.clearLogs(); // Clear logs after successful submission
      return report;
    } catch (error) {
      console.error('Failed to send bug report:', error);
      throw error;
    }
  }

  clearLogs(): void {
    this.consoleLog = [];
    this.networkLogs = [];
  }

  /**
   * Submits a bug report automatically without user interaction
   * Used for automatic error reporting in the application
   * @param description Description of the error that occurred
   * @returns Promise that resolves when the bug report is submitted
   */
  async submitAutomaticBugReport(description: string): Promise<BugReport | null> {
    // Skip automatic bug report submission in development mode
    if (!environment.production) {
      
      return null;
    }

    try {
      // Capture screenshot in a more performance-friendly way
      // Use requestAnimationFrame to ensure we capture at an appropriate time
      const screenshot = await new Promise<string>((resolve) => {
        requestAnimationFrame(async () => {
          try {
            // Take a screenshot of the current page with performance optimizations
            const canvas = await html2canvas(document.body, {
              logging: false,
              useCORS: true,
              allowTaint: true,
              scale: window.devicePixelRatio, // Ensure proper resolution
              backgroundColor: null, // Transparent background
              // Performance optimizations:
              removeContainer: true, // Clean up after capture
              foreignObjectRendering: false, // Can be faster in some browsers
              // Only capture the viewport to reduce processing time
              windowWidth: window.innerWidth,
              windowHeight: window.innerHeight
            });
            
            // Convert to data URL with medium quality to balance size and quality
            const screenshot = canvas.toDataURL('image/jpeg', 0.85);
            resolve(screenshot);
          } catch (err) {
            console.error('Screenshot capture failed:', err);
            resolve(''); // Resolve with empty string if screenshot fails
          }
        });
      });
      
      // Create the bug report input
      const bugReportInput: CreateBugReportInput = {
        description: `[AUTO] ${description} (v${environment.version})`,
        screenshot: screenshot || null, // Handle case where screenshot might fail
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        consoleLog: [],
        networkLogs: []
      };
      
      // Submit the bug report
      const report = await this.createBugReport(bugReportInput);
      
      return report;
    } catch (error) {
      console.error('Failed to submit automatic bug report:', error);
      return null;
    }
  }
}
